
import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useBreeds } from '@/hooks/useBreeds';

interface BreedSelectorProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const BreedSelector: React.FC<BreedSelectorProps> = ({
  value,
  onValueChange,
  placeholder = "Select breed...",
  className,
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const { data: breeds, isLoading } = useBreeds();

  const selectedBreed = breeds?.find((breed) => breed.name === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("justify-between", disabled ? "bg-gray-100 text-gray-500" : "", className)}
        >
          {selectedBreed ? selectedBreed.display_name : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search breeds..." />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Loading breeds..." : "No breed found."}
            </CommandEmpty>
            <CommandGroup>
              {breeds?.map((breed) => (
                <CommandItem
                  key={breed.id}
                  value={breed.display_name}
                  onSelect={() => {
                    if (!disabled) {
                      onValueChange(breed.name === value ? "" : breed.name);
                      setOpen(false);
                    }
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === breed.name ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {breed.display_name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
